<h2 mat-dialog-title>
  {{
    (isEdit
      ? 'epd.functions.careplan-nnn.intervention-dialog.edit-title'
      : 'epd.functions.careplan-nnn.intervention-dialog.add-title'
    )
      | translate
      | capitalize
  }}
</h2>
<mat-dialog-content class="intervention-dialog">
  @if (!isEdit) {
    <div class="picker">
      @if (showFilterToggle()) {
        <mat-button-toggle-group
          class="picker__filter"
          hideSingleSelectionIndicator
          [value]="interventionFilter()"
          (change)="onFilterChange($event.value)">
          <mat-button-toggle value="linked">
            {{ 'epd.functions.careplan-nnn.intervention-dialog.filter-linked' | translate | capitalize }}
          </mat-button-toggle>
          <mat-button-toggle value="all">
            {{ 'epd.functions.careplan-nnn.intervention-dialog.filter-all' | translate | capitalize }}
          </mat-button-toggle>
        </mat-button-toggle-group>
      }
      <mat-form-field appearance="outline" class="picker__search" [subscriptSizing]="'dynamic'">
        <input
          matInput
          type="text"
          [value]="searchText()"
          (input)="onSearchInput($event)"
          placeholder="{{
            'epd.functions.careplan-nnn.intervention-dialog.search-placeholder' | translate | capitalize
          }}" />
        <mat-icon matPrefix>search</mat-icon>
      </mat-form-field>
      <div class="progress-slot">
        @if (loadingList() || loadingTree()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>
      @if (showTree()) {
        <mat-accordion class="picker__tree">
          @for (domein of nicDomeinen(); track domein.key) {
            <mat-expansion-panel
              class="tree-panel tree-panel--domein"
              [expanded]="isDomeinExpanded(domein.key)"
              (opened)="setDomeinExpanded(domein.key, true)"
              (closed)="setDomeinExpanded(domein.key, false)">
              <mat-expansion-panel-header class="tree-panel__header">
                <mat-panel-title class="tree-panel__title">
                  <span class="tree-panel__code">{{ domein.label }}</span>
                  <span class="tree-panel__label">{{ domein.omschrijving }}</span>
                </mat-panel-title>
              </mat-expansion-panel-header>
              @if (domein.definitie) {
                <p class="tree-panel__definitie">{{ domein.definitie }}</p>
              }
              <mat-accordion class="picker__klassen">
                @for (klasse of domein.klassen; track klasse.key) {
                  <mat-expansion-panel
                    class="tree-panel tree-panel--klasse"
                    [expanded]="isKlasseExpanded(klasse.key)"
                    (opened)="setKlasseExpanded(klasse, true)"
                    (closed)="setKlasseExpanded(klasse, false)">
                    <mat-expansion-panel-header class="tree-panel__header">
                      <mat-panel-title class="tree-panel__title">
                        <span class="tree-panel__code">{{ klasse.label }}</span>
                        <span class="tree-panel__label">{{ klasse.omschrijving }}</span>
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    @if (klasse.definitie) {
                      <p class="tree-panel__definitie">{{ klasse.definitie }}</p>
                    }
                    @if (isLoadingKlasse(klasse.key)) {
                      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                    } @else {
                      <ul class="item-list">
                        @for (interventie of itemsFor(klasse.key) ?? []; track interventie.code) {
                          <li>
                            <button
                              type="button"
                              class="item-list__item"
                              [class.item-list__item--selected]="interventie.code === selected()?.code"
                              (click)="select(interventie)">
                              <span class="item-list__code">{{ interventie.code }}</span>
                              <span class="item-list__label">{{ interventie.omschrijving }}</span>
                            </button>
                          </li>
                        } @empty {
                          <li class="item-list__empty">
                            {{ 'epd.functions.careplan-nnn.knowledge-base.no-items' | translate | capitalize }}
                          </li>
                        }
                      </ul>
                    }
                  </mat-expansion-panel>
                }
              </mat-accordion>
            </mat-expansion-panel>
          }
        </mat-accordion>
      } @else {
        <ul class="item-list picker__list">
          @for (interventie of filteredInterventies(); track interventie.code) {
            <li>
              <button
                type="button"
                class="item-list__item"
                [class.item-list__item--selected]="interventie.code === selected()?.code"
                (click)="select(interventie)">
                <span class="item-list__code">{{ interventie.code }}</span>
                <span class="item-list__label">{{ interventie.omschrijving }}</span>
              </button>
            </li>
          } @empty {
            @if (!loadingList()) {
              <li class="item-list__empty">
                {{ 'epd.functions.careplan-nnn.intervention-dialog.no-results' | translate | capitalize }}
              </li>
            }
          }
        </ul>
      }
    </div>
  }
  <div class="activities" [class.activities--full-width]="isEdit">
    @if (selected(); as interventie) {
      <h3 class="activities__title">
        <span class="activities__code">{{ interventie.code }}</span>
        {{ interventie.omschrijving }}
      </h3>
      <span class="activities__subtitle">
        {{ 'epd.functions.careplan-nnn.intervention-dialog.activities' | translate | capitalize }}
      </span>
      <div class="progress-slot">
        @if (loadingActiviteiten()) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
      </div>
      <ul class="activities__list">
        @for (activiteit of activiteiten(); track activiteit.code) {
          <li class="activities__item" [class.activities__item--selected]="isChecked(activiteit)">
            <mat-checkbox [checked]="isChecked(activiteit)" (change)="toggleActiviteit(activiteit)" color="primary">
              {{ activiteit.omschrijving }}
            </mat-checkbox>
          </li>
        } @empty {
          @if (!loadingActiviteiten()) {
            <li class="activities__empty">
              {{ 'epd.functions.careplan-nnn.intervention-dialog.no-activities' | translate | capitalize }}
            </li>
          }
        }
      </ul>
    } @else {
      <p class="activities__empty">
        {{ 'epd.functions.careplan-nnn.intervention-dialog.pick-first' | translate | capitalize }}
      </p>
    }
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-stroked-button mat-dialog-close>{{ 'general.words.cancel' | translate | capitalize }}</button>
  <button mat-flat-button color="primary" [disabled]="!selected()" [mat-dialog-close]="buildResult()">
    {{ (isEdit ? 'general.actions.save' : 'general.actions.add') | translate | capitalize }}
  </button>
</mat-dialog-actions>
